// src/components/SEO/SEO.tsx
import React from 'react';

const BASE_URL = 'https://www.filefusion.app';

interface FAQDataItem {
    question: string;
    answer: string;
}

interface SEOProps {
    title: string;
    description: string;
    keywords?: string;
    image?: string;
    url?: string;
    publishedDate?: string;
    modifiedDate?: string;
    author?: string;
    schema?: any | any[]; // Using any for schema objects as they can be quite varied
    faqData?: FAQDataItem[];
    canonical?: string;
    type?: 'website' | 'article' | 'course' | string;
    robotIndexing?: boolean;
    locale?: string;
    twitterHandle?: string;
    additionalMeta?: Record<string, string>;
    canonicalUrl?: string; // Legacy support from original code
}

/**
 * Enhanced SEO Component
 * Provides comprehensive meta tags for search engines and social media
 */
const SEO: React.FC<SEOProps> = ({
    title,
    description,
    keywords,
    image = '/og-image.png',
    url,
    publishedDate,
    modifiedDate,
    author = 'FileFusion',
    schema,
    faqData,
    canonical,
    type = 'website',
    robotIndexing = true,
    locale = 'en_US',
    twitterHandle = 'filefusion',
    additionalMeta = {},
    canonicalUrl: propCanonicalUrl
}) => {
    // Determine the current path for default canonicals
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

    // Industry Best Practice: Ensure we never index legacy "tutorials" paths
    const sanitizedPath = currentPath.replace(/\/insights\/tutorials\//g, '/insights/courses/');

    const finalCanonical = canonical || propCanonicalUrl || url || sanitizedPath;

    // Ensure absolute production URLs
    const getAbsoluteProductionUrl = (path: string | undefined): string => {
        if (!path) return BASE_URL;
        if (path.startsWith('http')) return path;
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${BASE_URL}${cleanPath}`;
    };

    const absoluteImage = getAbsoluteProductionUrl(image);
    const absoluteCanonical = getAbsoluteProductionUrl(finalCanonical);

    // Generate automatic Course schema if type is 'course'
    const courseSchema = type === 'course' ? {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": title,
        "description": description,
        "provider": {
            "@type": "Organization",
            "name": "FileFusion",
            "sameAs": BASE_URL
        }
    } : null;

    // Generate automatic Article schema if type is 'article'
    const articleSchema = type === 'article' ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "image": absoluteImage,
        "author": {
            "@type": "Organization",
            "name": author
        },
        "publisher": {
            "@type": "Organization",
            "name": "FileFusion",
            "logo": {
                "@type": "ImageObject",
                "url": getAbsoluteProductionUrl('/logo512.png')
            }
        },
        "datePublished": publishedDate || "2025-12-21T00:00:00Z",
        "dateModified": modifiedDate || publishedDate || "2025-12-21T00:00:00Z",
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": absoluteCanonical
        }
    } : null;

    // Generate FAQ schema if faqData is provided
    const faqSchema = faqData && faqData.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    } : null;

    // Combine all schemas
    const finalSchema: any[] = [];
    if (courseSchema) finalSchema.push(courseSchema);
    if (articleSchema) finalSchema.push(articleSchema);
    if (faqSchema) finalSchema.push(faqSchema);

    if (schema) {
        if (Array.isArray(schema)) {
            finalSchema.push(...schema);
        } else {
            finalSchema.push(schema);
        }
    }

    // Unified Head Management
    React.useEffect(() => {
        if (typeof document === 'undefined') return;

        // Helper to manage meta tags
        const setMeta = (attr: string, value: string, content: string | undefined): void => {
            let el = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement | null;
            if (!el) {
                if (!content) return; // Don't create if no content
                el = document.createElement('meta');
                el.setAttribute(attr, value);
                document.head.appendChild(el);
            }
            if (content) {
                el.setAttribute('content', content);
            } else {
                el.remove();
            }
        };

        // Helper to manage link tags
        const setLink = (rel: string, href: string | undefined, attr: string = 'rel', attrValue: string = rel): void => {
            let el = document.querySelector(`link[${attr}="${attrValue}"]`) as HTMLLinkElement | null;
            if (!el) {
                if (!href) return; // Don't create if no href
                el = document.createElement('link');
                el.setAttribute(attr, attrValue);
                document.head.appendChild(el);
            }
            if (href) {
                el.setAttribute('href', href);
            } else {
                el.remove();
            }
        };

        // 1. Primary
        document.title = title;
        setMeta('name', 'description', description);
        if (keywords) setMeta('name', 'keywords', keywords);

        // 2. Canonical & Alternates
        setLink('canonical', absoluteCanonical);
        setLink('alternate', absoluteCanonical, 'hrefLang', 'en');
        setLink('alternate', absoluteCanonical, 'hrefLang', 'x-default');

        // 3. Robots
        const robotContent = robotIndexing ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" : "noindex, nofollow";
        setMeta('name', 'robots', robotContent);
        setMeta('name', 'googlebot', robotIndexing ? "index, follow" : "noindex, nofollow");

        // 4. Open Graph
        setMeta('property', 'og:type', type === 'course' ? 'article' : type);
        setMeta('property', 'og:url', absoluteCanonical);
        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:image', absoluteImage);
        setMeta('property', 'og:site_name', 'FileFusion');

        // 5. Twitter
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:description', description);
        setMeta('name', 'twitter:image', absoluteImage);
        if (twitterHandle) setMeta('name', 'twitter:site', `@${twitterHandle}`);

    }, [absoluteCanonical, title, description, keywords, absoluteImage, type, robotIndexing, twitterHandle]);

    const schemasToRender = finalSchema.length === 0 ? null : (finalSchema.length === 1 ? finalSchema[0] : finalSchema);

    return (
        <>
            {/* Structured Data (JSON-LD) stays in component tree as it's valid anywhere */}
            {schemasToRender && Array.isArray(schemasToRender) ? (
                schemasToRender.map((schemaObj, index) => (
                    <script
                        key={`schema-${index}`}
                        type="application/ld+json"
                    >
                        {JSON.stringify(schemaObj)}
                    </script>
                ))
            ) : schemasToRender ? (
                <script type="application/ld+json">
                    {JSON.stringify(schemasToRender)}
                </script>
            ) : null}
        </>
    );
};

export default SEO;
